<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>{{ t('labels.editLabels') }} - {{ resourceName }}</h3>
        <button class="close-btn" @click="handleClose" :disabled="loading">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="modal-body">
        <div class="labels-container">
          <!-- Owner 标签 -->
          <div class="label-group">
            <label class="label-title">
              <i class="fas fa-user"></i>
              {{ t('labels.owner') }}
            </label>
            <div class="label-input-group">
              <input
                v-model="localLabels.owner"
                type="text"
                :placeholder="t('labels.ownerPlaceholder')"
                class="label-input"
                @blur="validateOwner"
                :disabled="loading"
              />
              <span class="label-suffix">@robotruck.jp</span>
            </div>
            <div v-if="ownerError" class="error-message">{{ ownerError }}</div>
          </div>

          <!-- Team 标签 -->
          <div class="label-group">
            <label class="label-title">
              <i class="fas fa-users"></i>
              {{ t('labels.team') }}
            </label>
            <select v-model="localLabels.team" class="label-select" :disabled="loading">
              <option value="">{{ t('labels.teamPlaceholder') }}</option>
              <option value="si-dev">SI Dev</option>
              <option value="algo-dev">Algo Dev</option>
              <option value="infra-dev">Infra Dev</option>
              <option value="hi">HI</option>
            </select>
          </div>

          <!-- Org 标签 -->
          <div class="label-group">
            <label class="label-title">
              <i class="fas fa-building"></i>
              {{ t('labels.org') }}
            </label>
            <select v-model="localLabels.org" class="label-select" :disabled="loading">
              <option value="">{{ t('labels.orgPlaceholder') }}</option>
              <option value="eng">Engineering</option>
              <option value="product">Product</option>
            </select>
          </div>
        </div>

        <!-- 当前标签显示 -->
        <div v-if="hasCurrentLabels" class="current-labels">
          <h5>{{ t('labels.editLabels') }}</h5>
          <div class="label-tags">
            <span
              v-if="currentLabels.owner"
              class="label-tag owner"
            >
              <i class="fas fa-user"></i>
              {{ formatOwnerForDisplay(currentLabels.owner) }}
            </span>
            <span
              v-if="currentLabels.team"
              class="label-tag team"
            >
              <i class="fas fa-users"></i>
              {{ getTeamDisplayName(currentLabels.team) }}
            </span>
            <span
              v-if="currentLabels.org"
              class="label-tag org"
            >
              <i class="fas fa-building"></i>
              {{ getOrgDisplayName(currentLabels.org) }}
            </span>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button
          @click="saveLabels"
          class="btn btn-primary"
          :disabled="loading || hasErrors"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-save"></i>
          {{ loading ? t('common.loading') : t('labels.saveLabels') }}
        </button>
        <button @click="resetLabels" class="btn btn-secondary" :disabled="loading">
          <i class="fas fa-undo"></i>
          {{ t('common.refresh') }}
        </button>
        <button @click="handleClose" class="btn btn-cancel" :disabled="loading">
          {{ t('common.cancel') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus';
import { useCloudProvider } from '../composables/useCloudProvider.js';

export default {
  name: 'LabelEditModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    resourceType: {
      type: String,
      required: true
    },
    resourceName: {
      type: String,
      required: true
    },
    zone: {
      type: String,
      required: true
    },
    currentLabels: {
      type: Object,
      default: () => ({})
    },
    projectId: {
      type: String,
      required: true
    }
  },
  emits: ['close', 'labels-updated'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { currentProvider } = useCloudProvider();
    const loading = ref(false);
    const ownerError = ref('');

    // 本地标签状态
    const localLabels = reactive({
      owner: '',
      team: '',
      org: ''
    });

    // 监听当前标签变化
    watch(() => props.currentLabels, (newLabels) => {
      // 将下划线转换为点号用于编辑框显示
      localLabels.owner = (newLabels.owner || '').replace(/_/g, '.');
      localLabels.team = newLabels.team || '';
      localLabels.org = newLabels.org || '';
    }, { deep: true, immediate: true });

    // 验证owner邮箱
    const validateOwner = () => {
      if (!localLabels.owner) {
        ownerError.value = '';
        return;
      }

      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const fullEmail = `${localLabels.owner}@robotruck.jp`;

      if (!emailRegex.test(fullEmail)) {
        ownerError.value = '请输入有效的邮箱地址';
      } else {
        ownerError.value = '';
      }
    };

    // 检查是否有错误
    const hasErrors = computed(() => {
      return !!ownerError.value;
    });

    // 检查是否有当前标签
    const hasCurrentLabels = computed(() => {
      return props.currentLabels.owner || props.currentLabels.team || props.currentLabels.org;
    });

    // 格式化owner显示
    const formatOwnerForDisplay = (owner) => {
      if (!owner) {return '';}
      let email = owner.replace(/_/g, '.');
      if (!email.endsWith('@robotruck.jp')) {
        email += '@robotruck.jp';
      }
      return email;
    };

    // 获取团队显示名称
    const getTeamDisplayName = (team) => {
      const teamNames = {
        'si-dev': 'SI Dev',
        'algo-dev': 'Algo Dev',
        'infra-dev': 'Infra Dev',
        'hi': 'HI'
      };
      return teamNames[team] || team;
    };

    // 获取组织显示名称
    const getOrgDisplayName = (org) => {
      const orgNames = {
        'eng': 'Engineering',
        'product': 'Product'
      };
      return orgNames[org] || org;
    };

    // 保存标签
    const saveLabels = async () => {
      if (hasErrors.value) {
        return;
      }

      loading.value = true;
      try {
        // 拼接完整邮箱并转换为GCP标签格式
        let ownerValue = localLabels.owner || '';
        // 转换为GCP标签格式：将@替换为_，只包含小写字母、数字、下划线和连字符
        if (ownerValue) {
          ownerValue = ownerValue.toLowerCase().replace(/[^a-z0-9_-]/g, '_');
        }

        // 长度校验
        if (ownerValue && ownerValue.length > 63) {
          ownerError.value = 'Owner标签不能超过63字符';
          loading.value = false;
          return;
        }
        if (localLabels.team && localLabels.team.length > 63) {
          ElMessage.error('Team标签不能超过63字符');
          loading.value = false;
          return;
        }
        if (localLabels.org && localLabels.org.length > 63) {
          ElMessage.error('Org标签不能超过63字符');
          loading.value = false;
          return;
        }

        const requestBody = {
          projectId: props.projectId,
          resourceType: props.resourceType,
          resourceName: props.resourceName,
          zone: props.zone,
          labels: {
            owner: ownerValue || null,
            team: localLabels.team || null,
            org: localLabels.org || null
          }
        };

        console.log('发送标签更新请求:', requestBody);

        // 根据云提供商选择API端点
        const endpoint = currentProvider.value === 'aws' ? '/api/aws/labels/update' : '/api/labels/update';

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (response.ok) {
          const result = await response.json();
          emit('labels-updated', result.labels);
          ElMessage.success('标签更新成功');
          emit('close');
        } else {
          const error = await response.json();
          throw new Error(error.error || '保存标签失败');
        }
      } catch (error) {
        console.error('保存标签失败:', error);
        ElMessage.error(error.message || '保存标签失败');
      } finally {
        loading.value = false;
      }
    };

    // 重置标签
    const resetLabels = () => {
      // 将下划线转换为点号用于编辑框显示
      localLabels.owner = (props.currentLabels.owner || '').replace(/_/g, '.');
      localLabels.team = props.currentLabels.team || '';
      localLabels.org = props.currentLabels.org || '';
      ownerError.value = '';
    };

    // 处理关闭
    const handleClose = () => {
      if (!loading.value) {
        emit('close');
      }
    };

    // 处理遮罩层点击
    const handleOverlayClick = () => {
      if (!loading.value) {
        emit('close');
      }
    };

    return {
      loading,
      ownerError,
      localLabels,
      hasErrors,
      hasCurrentLabels,
      validateOwner,
      formatOwnerForDisplay,
      getTeamDisplayName,
      getOrgDisplayName,
      saveLabels,
      resetLabels,
      handleClose,
      handleOverlayClick,
      t
    };
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover:not(:disabled) {
  background: #f5f5f5;
  color: #333;
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-body {
  padding: 24px;
}

.labels-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.label-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label-title {
  font-weight: 600;
  color: #555;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-title i {
  color: #007bff;
}

.label-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.label-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s;
}

.label-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.label-input:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.label-suffix {
  color: #666;
  font-size: 14px;
  white-space: nowrap;
}

.label-select {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: all 0.2s;
}

.label-select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.label-select:disabled {
  background: #f8f9fa;
  cursor: not-allowed;
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.current-labels {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e9ecef;
}

.current-labels h5 {
  margin: 0 0 12px 0;
  color: #555;
  font-size: 14px;
}

.label-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.label-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
}

.label-tag.owner {
  background: #d4edda;
  color: #155724;
}

.label-tag.team {
  background: #d1ecf1;
  color: #0c5460;
}

.label-tag.org {
  background: #fff3cd;
  color: #856404;
}

.label-tag i {
  font-size: 10px;
}

.modal-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #545b62;
}

.btn-cancel {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-cancel:hover:not(:disabled) {
  background: #e9ecef;
}

.fa-spin {
  animation: fa-spin 1s infinite linear;
}

@keyframes fa-spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
